import {
  ActionsInner,
  IdeasContent,
  QRCodeContent,
  Suggestion,
  SuggestionContent,
  SuggestionStatus,
  SuggestionType,
} from '@dazzle/api-client';

import { getS3Url } from 'Common/util/Photo';

export interface USuggestion {
  actions?: ActionsInner[];
  aiMessageStr: string;
  content?: SuggestionContent;
  createdTs?: string | null;
  lastUpdatedTs?: string | null;
  minPhotoTs?: string | null;
  sortKey?: number | null;
  status: SuggestionStatus | null;
  suggestionId: string;
  suggestionType: SuggestionType;
  title?: string | null;
  photoUrls?: string[];
  localAssetId?: string | null;
  qualityFeedback?: string | null;
}

export const fromSuggestion = (suggestion: Suggestion): USuggestion => {
  return {
    actions: suggestion.actions,
    aiMessageStr: suggestion.ai_message,
    content: suggestion.content ?? undefined,
    createdTs: suggestion.created_ts,
    lastUpdatedTs: suggestion.last_updated_ts,
    minPhotoTs: suggestion.min_photo_ts,
    sortKey: suggestion.sort_key,
    status: suggestion.status,
    suggestionId: suggestion.suggestion_id,
    suggestionType: suggestion.suggestion_type,
    title: suggestion.title,
    photoUrls: suggestion.photo_urls,
    qualityFeedback: suggestion.quality_feedback,
  };
};

export const toSuggestion = (uSuggestion: USuggestion): Suggestion => {
  return {
    actions: uSuggestion.actions,
    photo_urls: uSuggestion.photoUrls,
    ai_message: uSuggestion.aiMessageStr,
    content: uSuggestion.content,
    created_ts: uSuggestion.createdTs,
    last_updated_ts: uSuggestion.lastUpdatedTs,
    min_photo_ts: uSuggestion.minPhotoTs,
    sort_key: uSuggestion.sortKey,
    status: uSuggestion.status,
    suggestion_id: uSuggestion.suggestionId,
    suggestion_type: uSuggestion.suggestionType,
    title: uSuggestion.title,
    quality_feedback: uSuggestion.qualityFeedback as 'NONE' | 'GOOD' | 'NEUTRAL' | 'BAD' | undefined,
  };
};

export const prefetchImagesFromSuggestions = (suggestions: USuggestion[]) => {
  const firstIdeas = getSortedIdeas(suggestions).slice(0, 5);

  const firstNonIdeas = getSortedNonIdeas(suggestions).slice(0, 5);

  [...firstIdeas, ...firstNonIdeas].forEach((suggestion) => {
    suggestion.photoUrls?.forEach((url) => {
      new Image().src = getS3Url(url);
    });
  });
};

const sortBySortKeyDesc = (a: USuggestion, b: USuggestion): number => {
  return (b.sortKey ?? 0) - (a.sortKey ?? 0);
};

const sortByMinPhotoTsDesc = (a: USuggestion, b: USuggestion): number => {
  const aTs = a.minPhotoTs ? new Date(a.minPhotoTs).getTime() : 0;
  const bTs = b.minPhotoTs ? new Date(b.minPhotoTs).getTime() : 0;
  return bTs - aTs;
};

const isEventDateInFuture = (eventDate: string | undefined | null): boolean => {
  if (!eventDate) return true;
  const event = new Date(eventDate + 'T00:00:00');
  if (isNaN(event.getTime())) {
    return true;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return event >= today;
};

export const getSortedIdeas = (suggestions: USuggestion[]) => {
  return suggestions
    .sort(sortBySortKeyDesc)
    .filter((conv) => {
      if (conv.suggestionType !== SuggestionType.Ideas) return false;
      const content = conv.content as IdeasContent | undefined;
      if (!content?.external_image_url) return false;
      return isEventDateInFuture(content.event_date);
    });
};

export const getSortedNonIdeas = (suggestions: USuggestion[]) => {
  return suggestions.sort(sortByMinPhotoTsDesc).filter((conv) => {
    if (conv.suggestionType === SuggestionType.Qrcode) {
      return (
        typeof window !== 'undefined' &&
        window.ApplePaySession &&
        conv.content &&
        (conv.content as QRCodeContent).pass_url !== null
      );
    }

    return conv.suggestionType !== SuggestionType.Ideas && conv.suggestionType !== SuggestionType.SharePhotos;
  });
};
